'use client'

import React from 'react'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate } from '../ProcessedEntry/DomainEntry/processedTypes'
import type { EntryRow } from './entryTypes'

const STEP_LABELS: Record<string, string> = {
  classify_success: 'Classificação concluída',
  classify_failed: 'Falha na classificação',
  classify_manual_update: 'Classificação manual',
  pipeline_start: 'Pipeline iniciado',
  pipeline_end: 'Pipeline concluído',
  field_correction: 'Correção de campos',
}

const REVISION_LABELS: Record<string, { label: string; tone: 'success' | 'error' | 'info' }> = {
  accept: { label: 'Aceite', tone: 'success' },
  'accept-hidden': { label: 'Aceite (oculto)', tone: 'success' },
  reject: { label: 'Rejeitado', tone: 'error' },
  defer1: { label: 'Adiado', tone: 'info' },
}

type StageStatus = 'neutral' | 'success' | 'error' | 'info' | 'pending'

type Stage = { key: string; label: string; date?: string; status: StageStatus }

const DOT_CLS: Record<StageStatus, string> = {
  neutral: 'bg-signal border-signal',
  success: 'bg-success border-success',
  error: 'bg-destructive border-destructive',
  info: 'bg-signal border-signal',
  pending: 'bg-transparent border-border',
}

export function EntryTimeline({ item, theme = 'light' }: { item: EntryRow; theme?: ProcessedTheme }) {
  const c = watchClasses(theme)
  const raw = item.raw_entries ?? item
  const pipelineSteps = ((item.user_entry_pipeline_steps ?? (item as any).pipeline_steps ?? []) as any[])
    .slice()
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())

  const stages: Stage[] = []

  stages.push({
    key: 'received',
    label: 'Recebido',
    date: raw.created_at ?? item.created_at,
    status: 'neutral',
  })

  pipelineSteps.forEach((s) => {
    const isFailed = s.step?.includes('failed')
    const label = STEP_LABELS[s.step]
      ?? (s.step?.startsWith('classify_manual_update') ? 'Classificação manual'
        : s.step?.startsWith('field_correction') ? 'Correção de campos'
        : s.step)
    stages.push({
      key: s.step + s.created_at,
      label,
      date: s.created_at,
      status: isFailed ? 'error' : 'success',
    })
  })

  const revisionStatus = item.revision_status ?? ''
  const revisionDate = item.revision_date
  if (revisionStatus && revisionStatus !== 'unseen' && REVISION_LABELS[revisionStatus]) {
    const { label, tone } = REVISION_LABELS[revisionStatus]
    stages.push({ key: 'revision', label, date: revisionDate, status: tone })
  } else if (!item.posted) {
    stages.push({ key: 'pending', label: 'Aguarda revisão', status: 'pending' })
  }

  return (
    <div className="px-1 py-1">
      {stages.map((stage, i) => {
        const isLast = i === stages.length - 1
        return (
          <div key={stage.key} className="flex min-h-9">
            <div className="flex w-6 flex-col items-center">
              <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full border-2 ${DOT_CLS[stage.status]}`} />
              {!isLast && <span className={`mt-0.5 w-0.5 flex-1 bg-muted`} />}
            </div>
            <div className="flex-1 pb-3.5 pl-2.5">
              <div className={`text-[13px] ${c.text}`}>{stage.label}</div>
              {stage.date && <div className={`mt-0.5 text-[11px] ${c.faint}`}>{compactDate(stage.date)}</div>}
            </div>
          </div>
        )
      })}
    </div>
  )
}
